import { auth } from "../firebase";
import { onAuthStateChanged } from "firebase/auth";

import { API as API_BASE } from "../config/env";

// Token cache — avoid calling getIdToken() on every request
let _tokenCache = null;
let _tokenExpiry = 0;
let _tokenUserId = null;
const TOKEN_BUFFER_MS = 5 * 60 * 1000; // refresh 5 min before expiry

/** Wait until Firebase Auth has restored persistence (or timeout). Do not treat the first null as signed-out. */
export async function waitForAuthUser(timeoutMs = 8000) {
  if (auth.currentUser) return auth.currentUser;
  if (typeof auth.authStateReady === "function") {
    await auth.authStateReady();
    return auth.currentUser;
  }
  return new Promise((resolve) => {
    let settled = false;
    const finish = (user) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      unsub();
      resolve(user || null);
    };
    const timer = setTimeout(() => finish(auth.currentUser), timeoutMs);
    const unsub = onAuthStateChanged(auth, (user) => {
      if (user) finish(user);
    });
  });
}

async function _getToken() {
  let user = auth.currentUser;
  if (!user) {
    user = await waitForAuthUser();
  }
  if (!user) return null;
  const now = Date.now();
  if (_tokenCache && user.uid === _tokenUserId && now < _tokenExpiry - TOKEN_BUFFER_MS) {
    return _tokenCache;
  }
  const result = await user.getIdTokenResult();
  _tokenCache = result.token;
  _tokenExpiry = new Date(result.expirationTime).getTime();
  _tokenUserId = user.uid;
  return _tokenCache;
}

// Invalidate cache on auth state change (sign-out or user switch)
onAuthStateChanged(auth, (user) => {
  if (!user || user.uid !== _tokenUserId) {
    _tokenCache = null;
    _tokenExpiry = 0;
    _tokenUserId = null;
  }
});

/** Drop the in-memory ID token after claims change (select-org, role update). */
export function invalidateTokenCache() {
  _tokenCache = null;
  _tokenExpiry = 0;
  _tokenUserId = null;
}

export async function apiFetch(path, options = {}) {
  const { headers = {}, ...rest } = options;

  let token = null;
  try {
    token = await _getToken();
  } catch (err) {
    if (auth.currentUser) throw err;
  }

  const requestHeaders = {
    "Content-Type": "application/json",
    ...headers,
  };

  if (token) {
    requestHeaders["Authorization"] = `Bearer ${token}`;
  }

  return fetch(`${API_BASE}${path}`, {
    ...rest,
    headers: requestHeaders,
  });
}

/** FastAPI `{ detail }` or `{ message }` → a single string for toasts. */
export async function parseApiError(response, fallback = "Request failed") {
  const body = await response.json().catch(() => ({}));
  const detail = body?.detail;
  if (typeof detail === "string" && detail.trim()) return detail;
  if (Array.isArray(detail)) {
    const msgs = detail.map((d) => d?.msg || d?.message || "").filter(Boolean);
    if (msgs.length) return msgs.join("; ");
  }
  if (typeof body?.message === "string" && body.message.trim()) return body.message;
  return `${fallback} (${response.status})`;
}

/** Throw if the Response is not OK. apiFetch does not throw on 4xx/5xx. */
export async function requireOk(response, fallback = "Request failed") {
  if (response.ok) return response;
  throw new Error(await parseApiError(response, fallback));
}

export { API_BASE as API };
